import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/primeng';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggingService } from '../../services/logging.service';
import { LocalCacheManager } from '../managers/local-cache.manager';
import { DeviceGroup } from '../models/device-group.model';
import { SceneSchedules } from '../models/scene-schedules.model';
import { SceneStatus } from '../models/scene-status.model';
import { ApiUrlService } from './api-url.service';
import { ApiService } from './api.service';

@Injectable()
export class SceneApiService extends ApiService {
    constructor(protected http: HttpClient, protected apiUrlService: ApiUrlService, protected localCacheManager: LocalCacheManager,
        private messageService: MessageService) {
        super(http, localCacheManager);
    }

    getAllScenesByLocation(locationId: number): Observable<any> {
        return this.apiRequest(this.apiUrlService.getAllScenesByLocation(locationId), this.HttpMethod.Get);
    }

    getSceneStatuses(locationId: number): Observable<SceneStatus[]> {
        return this.apiRequest(this.apiUrlService.getSceneStatusUrl(locationId), this.HttpMethod.Get);
    }

    getThemesByScene(sceneId: number): Observable<any> {
        return this.apiRequest(this.apiUrlService.getThemesByScene(sceneId), this.HttpMethod.Get);
    }

    getSceneSchedules(sceneId: number): Observable<SceneSchedules> {
        return this.apiRequest(this.apiUrlService.getSceneSchedulesUrl(sceneId), this.HttpMethod.Get);
    }

    forceSceneOn(sceneId: number, duration: number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getForceSceneOnUrl(sceneId, duration), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response['WasSuccess'];
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error['error']['Message'] });
                    return of(false);
                })
            );
    }

    forceSceneOff(sceneId: number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getForceSceneOffUrl(sceneId), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response['WasSuccess'];
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error['error']['Message'] });
                    return of(false);
                })
            );
    }

    forceAllScenesOn(locationId: number, duration: number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getForceAllScenesOnUrl(locationId, duration), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response['WasSuccess'];
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    forceAllScenesOff(locationId: number): Observable<boolean> {
        return this.apiRequest(this.apiUrlService.getForceAllScenesOffUrl(locationId), this.HttpMethod.Put)
            .pipe(
                map((response: any) => {
                    return response['WasSuccess'];
                }),
                catchError((error: any) => {
                    LoggingService.log(error);
                    return of(false);
                })
            );
    }

    createScene(locationId: number, createSceneModel: { Scene: DeviceGroup, ScheduleSet: SceneSchedules }): Observable<any> {
        return this.apiRequest(this.apiUrlService.getCreateSceneUrl(locationId), this.HttpMethod.Post, createSceneModel);
    }

    updateScene(sceneId: number, updateScene: boolean, updateSchedules: boolean, updateSceneModel: { Scene: DeviceGroup, ScheduleSet: SceneSchedules }): Observable<any> {
        return this.apiRequest(this.apiUrlService.getUpdateSceneUrl(sceneId, updateScene, updateSchedules), this.HttpMethod.Post, updateSceneModel);
    }

    deleteScene(sceneId: number): Observable<any> {
        return this.apiRequest(this.apiUrlService.getDeleteSceneUrl(sceneId), this.HttpMethod.Post);
    }
}
